/**
 * Schema Markup Generators for Static Pages
 *
 * Provides functions to generate Schema.org JSON-LD markup for different page types.
 * Following Schema.org standards for structured data.
 *
 * Performance: Implements in-memory caching to avoid regenerating identical schemas.
 */

import { SchemaMarkup } from '../../../interfaces/seo';

/**
 * In-memory cache for generated schemas
 * Key format: "pageName:customerName:baseUrl"
 * Cleared when configuration changes
 */
const schemaCache = new Map<string, SchemaMarkup | SchemaMarkup[]>();

/**
 * Configuration for schema generators
 */
export interface SchemaGeneratorConfig {
  customerName: string;
  baseUrl?: string;
  logoUrl?: string;
  contactEmail?: string;
}

/**
 * Generates Organization schema for home page
 * @see https://schema.org/Organization
 */
export function generateOrganizationSchema(config: SchemaGeneratorConfig): SchemaMarkup {
  const schema: SchemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: config.customerName,
  };

  if (config.baseUrl) {
    schema.url = config.baseUrl;
  }

  if (config.logoUrl) {
    schema.logo = config.logoUrl;
  }

  if (config.contactEmail) {
    schema.contactPoint = {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: config.contactEmail,
    };
  }

  return schema;
}

/**
 * Generates WebSite schema for home page with search action
 * @see https://schema.org/WebSite
 */
export function generateWebSiteSchema(config: SchemaGeneratorConfig): SchemaMarkup {
  const schema: SchemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: config.customerName,
  };

  if (config.baseUrl) {
    schema.url = config.baseUrl;
    schema.potentialAction = {
      '@type': 'SearchAction',
      target: `${config.baseUrl}/destination?search={search_term_string}`,
      'query-input': 'required name=search_term_string',
    };
  }

  return schema;
}

/**
 * Generates FAQPage schema
 * @see https://schema.org/FAQPage
 */
export function generateFAQPageSchema(
  config: SchemaGeneratorConfig,
  faqs?: Array<{ question: string; answer: string }>
): SchemaMarkup {
  const schema: SchemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    name: `${config.customerName} FAQ`,
  };

  if (faqs && faqs.length > 0) {
    schema.mainEntity = faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    }));
  }

  return schema;
}

/**
 * Generates HowTo schema for installation/guide pages
 * @see https://schema.org/HowTo
 */
export function generateHowToSchema(
  config: SchemaGeneratorConfig,
  title: string,
  steps?: Array<{ name: string; text: string }>
): SchemaMarkup {
  const schema: SchemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: title,
  };

  if (steps && steps.length > 0) {
    schema.step = steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    }));
  }

  return schema;
}

/**
 * Generates generic WebPage schema for other pages
 * @see https://schema.org/WebPage
 */
export function generateWebPageSchema(
  config: SchemaGeneratorConfig,
  pageTitle: string,
  pageDescription: string,
  pageUrl?: string
): SchemaMarkup {
  const schema: SchemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description: pageDescription,
  };

  // Only add URL if provided
  if (pageUrl) {
    schema.url = pageUrl;
  }

  if (config.customerName) {
    schema.publisher = {
      '@type': 'Organization',
      name: config.customerName,
    };
  }

  return schema;
}

/**
 * Determines which schema generator to use based on page name
 * Returns appropriate schema markup for the page type
 *
 * Performance: Caches generated schemas ONLY for static pages.
 * Dynamic pages (countries, regions) generate their own Product schemas
 * which should NOT be cached as they contain dynamic data (prices, plans).
 *
 * Cache key is based on stable configuration values (customerName, baseUrl).
 */
export function generateSchemaForPage(
  pageName: string,
  config: SchemaGeneratorConfig,
  title: string,
  description: string,
  path: string
): SchemaMarkup | SchemaMarkup[] | undefined {
  // Define which pages should have cached schemas
  // Only static pages with stable content should be cached
  const CACHEABLE_PAGES = ['home', 'faq', 'installation-info', 'how-it-works'];
  const isCacheable = CACHEABLE_PAGES.includes(pageName);

  // Check cache first (only for cacheable pages)
  if (isCacheable) {
    const cacheKey = `${pageName}:${config.customerName}:${config.baseUrl}`;
    if (schemaCache.has(cacheKey)) {
      return schemaCache.get(cacheKey);
    }
  }

  // Generate schema based on page type
  let schema: SchemaMarkup | SchemaMarkup[] | undefined;

  switch (pageName) {
    case 'home':
      // Home page gets both Organization and WebSite schemas
      schema = [generateOrganizationSchema(config), generateWebSiteSchema(config)];
      break;

    case 'faq':
      schema = generateFAQPageSchema(config);
      break;

    case 'installation-info':
    case 'how-it-works':
      schema = generateHowToSchema(config, title);
      break;

    default:
      // Generic WebPage schema for other pages
      // Note: This is for static pages only. Dynamic pages (countries, regions)
      // generate their own Product schemas in generateLocationSeo()
      if (title && description) {
        const url = config.baseUrl ? `${config.baseUrl}${path}` : undefined;
        schema = generateWebPageSchema(config, title, description, url);
      }
      break;
  }

  // Cache the result ONLY for static pages
  if (schema && isCacheable) {
    const cacheKey = `${pageName}:${config.customerName}:${config.baseUrl}`;
    schemaCache.set(cacheKey, schema);
  }

  return schema;
}

/**
 * Clears the schema cache
 * Should be called when configuration changes (e.g., customer name, base URL)
 */
export function clearSchemaCache(): void {
  schemaCache.clear();
}

/**
 * Gets the current cache size (for monitoring/debugging)
 */
export function getSchemaCacheSize(): number {
  return schemaCache.size;
}
